<template>
  <div class="question-form" :class="{ 'is-focused': isAnswerInputFocused }">
    <el-tooltip v-if="editable" content="Click to toggle help" placement="top">
      <div class="toggle-help">
        <a @click.prevent="toggleHelp()"><i class="ion ion-help"></i></a>
      </div>
    </el-tooltip>
    <el-tooltip
      v-if="editable && canEditQuestion"
      content="Click to remove this question"
      placement="top"
    >
      <div class="remove">
        <a @click.prevent="removeQuestion(elementId, sectionId)"
          ><i class="ion ion-close"></i
        ></a>
      </div>
    </el-tooltip>

    <template v-if="editable">
      <TextEditor
        v-if="canEditQuestion"
        class="question"
        bottom-border
        seamless
        placeholder="Question"
        :text="element.question"
        @blur="update('question', $event, elementId, sectionId)"
      />
      <div v-else class="question" v-html="element.question"></div>
      <div
        v-if="isTipVisible && questionTip"
        class="question-tip"
        v-html="questionTip"
      />
      <TextEditor
        class="answer"
        bottom-border
        seamless
        placeholder="Answer"
        :text="element.answer"
        :tools="[
          'bold',
          'italic',
          'underline',
          'anchor',
          'orderedlist',
          'unorderedlist',
        ]"
        @focus="onAnswerInputFocus()"
        @blur="
          update('answer', $event, elementId, sectionId)
          onAnswerInputBlur()
        "
      />
    </template>

    <template v-if="!editable && hasText">
      <div class="question question-preview" v-html="element.question"></div>
      <div class="answer answer-preview" v-html="element.answer"></div>
    </template>
  </div>
</template>

<script>
import TextEditor from '@/components/TextEditor'
import {
  isInitialQuestion,
  getQuestionTip,
} from '@/services/faq-initial-sections'
import { mapState } from 'vuex'
import { htmlElementHasText } from '@/utils'
import { htmlToText } from '@/utils/forms'

export default {
  components: { TextEditor },
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
    elementId: {
      type: Number,
      default: null,
    },
    sectionId: {
      type: Number,
      default: null,
    },
    section: { type: Object, default: () => ({}) },
    element: {
      type: Object,
      default: () => ({
        question: '',
        answer: '',
        help: null,
      }),
    },
  },
  computed: {
    ...mapState({
      user: (state) => state.user.data,
    }),
    isInitial() {
      return isInitialQuestion({
        sectionName: this.section.section,
        question: this.element.question,
      })
    },
    canEditQuestion() {
      return user.isAdmin || !this.isInitial
    },
    hasText() {
      return htmlElementHasText(this.element.answer)
    },
    questionTip() {
      const tip = getQuestionTip({
        sectionName: this.section.section,
        question: htmlToText(this.element.question),
      })
      return tip
    },
  },
  data() {
    return {
      isAnswerInputFocused: false,
      isTipVisible: false,
    }
  },
  methods: {
    update(property, value, elementId, sectionId) {
      this.$store.commit(
        `startupProfile/UPDATE_FAQ_${property.toUpperCase()}`,
        { value, elementId, sectionId },
      )
    },
    async removeQuestion(elementId, sectionId) {
      try {
        await this.$confirm(
          'Are you sure you want to remove this question?',
          'Confirm deletion',
        )
      } catch (e) {
        // do nothing if cancelled
        return
      }
      this.$store.commit('startupProfile/REMOVE_FAQ_ELEMENT', {
        elementId,
        sectionId,
      })
      this.$message({
        type: 'success',
        message: 'Question removed',
      })
    },
    onAnswerInputFocus() {
      this.isAnswerInputFocused = true
      this.isTipVisible = true
    },
    onAnswerInputBlur() {
      this.isAnswerInputFocused = false
      this.isTipVisible = false
    },
    onTipClose() {
      const $input = this.$el.querySelector('.answer')
      if ($input) {
        $input.focus()
      }
      // following line would disable popover from showing up
      // but it should re-open after user changes focus to another input
      // and back to this one
      this.isTipVisible = false
    },
    toggleHelp() {
      this.isTipVisible = !this.isTipVisible
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.faq-section {
  .question-form {
    margin: 16px 0 24px;
    position: relative;
    padding: 5px;
    &:hover {
      .toggle-help,
      .remove {
        display: block;
      }
    }

    &.is-focused {
      border: 1px solid $grey150;
      border-radius: 4px;
      // background-color: #fcfcfc;
      // background-color: $blue;
    }

    .question-tip {
      margin: 10px 6px;
      font-size: 14px;
      font-weight: 400;
      font-style: italic;
      color: $grey120;
    }

    .question {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 1.2rem;
      resize: none;
      color: $grey30;
    }

    .question-preview {
      > p {
        margin: 0;
      }
    }

    .answer {
      margin-bottom: 1rem;
      font-size: 16px;
      p {
        margin: 0 0 10px;
      }

      li {
        margin: 6px 0;
      }
    }

    .vb-text-editor {
      font-size: 16px;
    }
  }

  .remove {
    cursor: pointer;
    position: absolute;
    right: 12px;
    z-index: 1;
    display: none;
  }
  .toggle-help {
    cursor: pointer;
    position: absolute;
    right: 32px;
    z-index: 1;
    display: none;
  }

  .tip-popover {
    position: absolute;
    display: inline-block;
    width: 100%;
    height: 100%;
    z-index: -1;
  }
}

.el-popover {
  .close-popover {
    position: absolute;
    top: 10px;
    right: 10px;

    &:hover {
      color: $bright;
      cursor: pointer;
    }
  }
}
</style>
